'use client';

import * as React from 'react';
import { CircularProgress, Typography } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import type { TableProps } from '@mui/material/Table';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Box, Stack } from '@mui/system';

import HelpInfoPopover from './help-info-popover';
import SortWidget from './sort-widget';

export interface ColumnDef<TRowModel> {
  align?: 'left' | 'right' | 'center';
  field?: keyof TRowModel;
  formatter?: (row: TRowModel, index: number) => React.ReactNode | string;
  hideName?: boolean;
  name: string;
  width?: number | string;
  sortableKey?: string;
  sortType?: 'string' | 'number';
  description?: string;
  descriptionType?: string;
}

type RowId = number | string;

export interface DataTableProps<TRowModel> extends Omit<TableProps, 'onClick'> {
  columns: ColumnDef<TRowModel>[];
  hideHead?: boolean;
  hover?: boolean;
  onClick?: (event: React.MouseEvent, row: TRowModel) => void;
  onDeselectAll?: (event: React.ChangeEvent) => void;
  onDeselectOne?: (event: React.ChangeEvent, row: TRowModel) => void;
  onSelectAll?: (event: React.ChangeEvent) => void;
  onSelectOne?: (event: React.ChangeEvent, row: TRowModel) => void;
  rows: TRowModel[];
  selectable?: boolean;
  selected?: Set<RowId>;
  uniqueRowId?: (row: TRowModel) => RowId;
  isLoading?: boolean;
}

export function DataTable<TRowModel extends object & { id?: RowId | null }>({
  columns,
  hideHead,
  hover,
  onClick,
  onDeselectAll,
  onDeselectOne,
  onSelectOne,
  onSelectAll,
  rows,
  selectable,
  selected,
  uniqueRowId,
  isLoading,
  ...props
}: DataTableProps<TRowModel>): React.JSX.Element {
  const selectedSome = (selected?.size ?? 0) > 0 && (selected?.size ?? 0) < rows.length;
  const selectedAll = rows.length > 0 && selected?.size === rows.length;

  return (
    <Table {...props}>
      <TableHead sx={{ ...(hideHead && { visibility: 'collapse', '--TableCell-borderWidth': 0 }) }}>
        <TableRow>
          {selectable ? (
            <TableCell
              padding="checkbox"
              sx={{
                width: '40px',
                minWidth: '40px',
                maxWidth: '40px',
                backgroundColor: 'var(--mui-palette-primary-800) !important',
                color: 'var(--mui-palette-primary-100) !important',
              }}
            >
              <Checkbox
                checked={selectedAll}
                indeterminate={selectedSome}
                onChange={(event: React.ChangeEvent) => {
                  if (selectedAll) {
                    onDeselectAll?.(event);
                  } else {
                    onSelectAll?.(event);
                  }
                }}
              />
            </TableCell>
          ) : null}
          {columns.map(
            (column, index): React.JSX.Element => (
              <TableCell
                key={column.name}
                sx={{
                  width: column.width,
                  minWidth: column.width,
                  maxWidth: column.width,
                  ...(column.align && { textAlign: column.align }),
                  backgroundColor: 'var(--mui-palette-primary-800) !important',
                  color: 'var(--mui-palette-primary-100) !important',
                  borderRight: index !== columns.length ? '1px solid var(--mui-palette-divider)' : undefined,
                }}
              >
                <Stack
                  direction="row"
                  gap={0.5}
                  alignItems="center"
                  sx={{ textWrap: 'nowrap', justifyContent: column.align ? column.align : 'start' }}
                >
                  {column.sortableKey ? (
                    <SortWidget sortKey={column.sortableKey} sortType={column.sortType}>
                      <Stack
                        sx={{
                          minWidth: 'initial',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'left',
                          gap: 0.5,
                        }}
                      >
                        {column.hideName ? null : column.name}
                        {column.description ? (
                          <HelpInfoPopover
                            content={
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: column.description,
                                }}
                              />
                            }
                            color="gray"
                          />
                        ) : null}
                      </Stack>
                    </SortWidget>
                  ) : column.hideName ? null : (
                    column.name
                  )}
                </Stack>
              </TableCell>
            )
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {!rows.length && !isLoading ? (
          <TableRow>
            <TableCell colSpan={columns.length} align="center" sx={{ p: 6 }}>
              <Box
                alt="Not found"
                component="img"
                src="/assets/not-found.svg"
                sx={{ height: 'auto', maxWidth: '100%', width: '100px' }}
              />
              <Typography color="text.secondary" sx={{ textAlign: 'center' }} variant="body2">
                No Items found
              </Typography>
            </TableCell>
          </TableRow>
        ) : null}

        {isLoading ? (
          <TableRow>
            <TableCell colSpan={columns.length} align="center" sx={{ p: 6 }}>
              <CircularProgress color="primary" size={38} />
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row, index): React.JSX.Element => {
            const rowId = row.id ? row.id : uniqueRowId?.(row);
            const rowSelected = rowId ? selected?.has(rowId) : false;

            return (
              <TableRow
                hover={hover}
                key={rowId ?? index}
                selected={rowSelected}
                {...(onClick && {
                  onClick: (event: React.MouseEvent) => {
                    onClick(event, row);
                  },
                })}
                sx={{ ...(onClick && { cursor: 'pointer' }) }}
              >
                {selectable ? (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={rowId ? rowSelected : false}
                      onChange={(event: React.ChangeEvent) => {
                        if (rowSelected) {
                          onDeselectOne?.(event, row);
                        } else {
                          onSelectOne?.(event, row);
                        }
                      }}
                      onClick={(event: React.MouseEvent) => {
                        if (onClick) {
                          event.stopPropagation();
                        }
                      }}
                    />
                  </TableCell>
                ) : null}
                {columns.map(
                  (column): React.JSX.Element => (
                    <TableCell
                      key={column.name}
                      sx={{
                        ...(column.align && { textAlign: column.align }),
                        textWrap: 'nowrap',
                        maxWidth: column.width,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {
                        (column.formatter
                          ? column.formatter(row, index)
                          : column.field
                            ? row[column.field]
                            : null) as React.ReactNode
                      }
                    </TableCell>
                  )
                )}
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
